// In-depth gaussian-splat inspector — the tool to tell a MODEL problem (floaters, blown-out
// scales, faint opacity, wrong scene size) apart from a VIEWER problem. Renders the trained
// model two independent ways:
//   • Point cloud — the raw gaussian CENTERS, colored by SH0, drawn with our own Three.js
//     points (no splat renderer involved). If this looks like the room, the geometry is good.
//   • Splat — drei's faithful 3DGS renderer streaming the same .ply. If this looks good but the
//     iOS MetalSplatter view looks bad, the problem is the app's viewer, not the model.
// A stats panel quantifies the model so we're not guessing.

import { Suspense, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Canvas, useThree } from "@react-three/fiber";
import { OrbitControls, Splat, useGLTF } from "@react-three/drei";
import * as THREE from "three";
import { useStore } from "../lib/storeContext";
import { getSplatJob, type SplatJob, type SplatModelVariant } from "../lib/apiClient";
import type { RoomPayload, ScanPayload } from "../lib/types";
import type { GaussianStats } from "../splat/plyGaussians";
import { applyUnlitBakedMaterials } from "../dollhouse/RoomMeshGLB";
import { Breadcrumbs, EmptyState, Spinner } from "./ui";

function PointCloud({ positions, colors, size }: { positions: Float32Array; colors: Float32Array; size: number }) {
  const geometry = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    g.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    g.computeBoundingSphere();
    return g;
  }, [positions, colors]);
  useEffect(() => () => geometry.dispose(), [geometry]);
  return (
    <points geometry={geometry} frustumCulled={false}>
      <pointsMaterial vertexColors size={size} sizeAttenuation />
    </points>
  );
}

// W4: the textured polygon MESH derived from the splat (room-mesh.glb). The surface textures are BAKED
// appearance (they already contain the room's lighting), so we render them UNLIT (MeshBasicMaterial) —
// otherwise scene lighting double-darkens them (the "totally dark" bug). DoubleSide so interior walls
// are visible from inside, and we drop the camera INSIDE the room (orbiting it from outside just shows
// dark exterior wall backs).
function RoomMesh({ url }: { url: string }) {
  const { scene } = useGLTF(url);
  const camera = useThree((s) => s.camera);
  const controls = useThree((s) => s.controls) as unknown as { target: THREE.Vector3; update: () => void } | null;
  useEffect(() => {
    applyUnlitBakedMaterials(scene); // shared with the dollhouse RoomMeshGLB layer
    const box = new THREE.Box3().setFromObject(scene);
    const c = box.getCenter(new THREE.Vector3());
    const sz = box.getSize(new THREE.Vector3());
    // sit just off the room centre at ~eye height, look across the room
    camera.position.set(c.x + sz.x * 0.18, c.y + sz.y * 0.06, c.z + sz.z * 0.18);
    camera.near = 0.01;
    camera.far = Math.max(sz.x, sz.y, sz.z) * 20;
    camera.updateProjectionMatrix();
    if (controls) { controls.target.copy(c); controls.update(); }
  }, [scene, camera, controls]);
  return <primitive object={scene} />;
}

const fmtPct = (f: number) => `${(f * 100).toFixed(1)}%`;
const fmtN = (n: number) => n.toLocaleString();

function StatRow({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1">
      <span className="text-xs text-muted">{label}</span>
      <span className={`text-sm font-medium ${warn ? "text-red-500" : "text-ink"}`}>{value}</span>
    </div>
  );
}

interface Parsed {
  positions: Float32Array;
  colors: Float32Array;
  /** Packed antimatter15 .splat bytes for drei <Splat> (PLY → .splat done in the worker). */
  splat: Uint8Array;
  /** PCA up (gravity) in the model frame — orients the orbit camera. */
  up: [number, number, number];
  stats: GaussianStats;
}

export function SplatInspectorView() {
  // Two entry points: /scans/:scanId/splat (resolve the job via the synced scan) and
  // /splats/:jobId/splat (a standalone job, e.g. a local-only image-splat / Rich Capture that
  // has no synced scan). The jobId param wins when present.
  const { scanId = "", jobId: jobIdParam } = useParams();
  const { store, loading } = useStore();
  const scan = scanId ? store.get<ScanPayload>("SCAN", scanId) : undefined;
  const room = scan?.parentId ? store.get<RoomPayload>("ROOM", scan.parentId) : undefined;
  const jobId = jobIdParam ?? scan?.payload.splatJobId ?? null;

  const [job, setJob] = useState<SplatJob | null>(null);
  const [jobErr, setJobErr] = useState<string | null>(null);
  const [variant, setVariant] = useState<SplatModelVariant | null>(null);
  const [mode, setMode] = useState<"points" | "splat" | "mesh">("points");
  const [size, setSize] = useState(0.012);
  const [flipUp, setFlipUp] = useState(false); // manual override if the auto-detected up is upside-down
  const [data, setData] = useState<Parsed | null>(null);
  const [progress, setProgress] = useState(0);
  const [parseErr, setParseErr] = useState<string | null>(null);

  // 1. Resolve the splat job → presigned model URLs.
  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    setJobErr(null);
    getSplatJob(jobId)
      .then((j) => {
        if (cancelled) return;
        setJob(j);
        const first = j.models?.[0] ?? (j.modelUrl ? { id: "model.ply", label: "Default", bytes: null, url: j.modelUrl } : null);
        setVariant(first);
      })
      .catch((e) => !cancelled && setJobErr(e instanceof Error ? e.message : String(e)));
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  // 2. Download + parse the selected variant (drives the point cloud + stats + camera fit).
  useEffect(() => {
    if (!variant) return;
    let cancelled = false;
    setData(null);
    setParseErr(null);
    setProgress(0);
    const worker = new Worker(new URL("../splat/plyGaussians.worker.ts", import.meta.url), { type: "module" });
    worker.onmessage = (e: MessageEvent) => {
      if (cancelled) return;
      if (e.data.ok) setData({ positions: e.data.positions, colors: e.data.colors, splat: e.data.splat, up: e.data.up, stats: e.data.stats });
      else setParseErr(e.data.error);
      worker.terminate();
    };
    (async () => {
      try {
        const resp = await fetch(variant.url);
        if (!resp.ok) throw new Error(`download failed (${resp.status})`);
        const total = variant.bytes ?? Number(resp.headers.get("content-length")) ?? 0;
        let buf: ArrayBuffer;
        if (resp.body && total > 0) {
          const reader = resp.body.getReader();
          const chunks: Uint8Array[] = [];
          let got = 0;
          for (;;) {
            const { done, value } = await reader.read();
            if (done) break;
            chunks.push(value);
            got += value.length;
            if (!cancelled) setProgress(got / total);
          }
          const merged = new Uint8Array(got);
          let o = 0;
          for (const c of chunks) {
            merged.set(c, o);
            o += c.length;
          }
          buf = merged.buffer;
        } else {
          buf = await resp.arrayBuffer();
        }
        if (!cancelled) worker.postMessage(buf, [buf]);
      } catch (e) {
        if (!cancelled) setParseErr(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
      worker.terminate();
    };
  }, [variant]);

  // Camera framing from the floater-robust core box, oriented to the PCA up so the orbit
  // pole is the room's true vertical — fixes the "weird/rocking" spin (default Y-up orbiting
  // around a tilted COLMAP frame). flipUp negates it if the auto-detected up is upside-down.
  const view = useMemo(() => {
    if (!data) return null;
    const c = data.stats.centroid;
    const r = Math.max(0.5, 0.5 * Math.max(...data.stats.coreDims));
    const up = new THREE.Vector3(data.up[0], data.up[1], data.up[2]).normalize();
    if (flipUp) up.negate();
    // A horizontal viewing direction (perpendicular to up) to place the camera off to one side.
    let horiz = new THREE.Vector3(1, 0, 0);
    if (Math.abs(up.dot(horiz)) > 0.9) horiz = new THREE.Vector3(0, 0, 1);
    horiz.sub(up.clone().multiplyScalar(up.dot(horiz))).normalize();
    const center = new THREE.Vector3(c[0], c[1], c[2]);
    const position = center.clone()
      .add(horiz.clone().multiplyScalar(r * 2.2))
      .add(up.clone().multiplyScalar(r * 0.5));
    return { center, position, up, far: r * 50 };
  }, [data, flipUp]);

  // drei <Splat> needs the packed .splat format (NOT raw 3DGS PLY), so feed it the bytes we
  // packed during parse via a blob URL.
  const splatUrl = useMemo(
    () => (data?.splat ? URL.createObjectURL(new Blob([data.splat as BlobPart])) : null),
    [data?.splat],
  );
  useEffect(() => () => { if (splatUrl) URL.revokeObjectURL(splatUrl); }, [splatUrl]);

  if (loading) return <Spinner />;
  // Only a /scans/:scanId route requires a synced scan; the /splats/:jobId route inspects a
  // job directly (e.g. a local-only image-splat that has no synced scan).
  if (scanId && !scan) return <EmptyState title="Scan not found" icon="🔍" />;
  if (!jobId) {
    return (
      <EmptyState
        title="No photoreal model"
        subtitle="This scan hasn't been trained into a gaussian splat yet."
        icon="✦"
      />
    );
  }

  const s = data?.stats;
  // NOTE: ns-export writes the scene in NORMALIZED units, not meters — so absolute sizes are
  // meaningless. The useful signals are scale-INVARIANT: shape (flatness), outlier spread, and
  // the cull-style floater/opacity ratios.
  const maxDim = s ? Math.max(...s.dims) : 0;
  const minDim = s ? Math.min(...s.dims) : 0;
  const maxCore = s ? Math.max(...s.coreDims) : 0;
  // A real room is roughly as tall as a fraction of its footprint; if the smallest extent is a
  // tiny fraction of the largest, the walls/ceiling reconstructed THIN (a flat, floor-dominated
  // slab) — the classic "looks fuzzy from inside" failure.
  const flatness = s && maxDim > 0 ? minDim / maxDim : 1;
  const flat = s ? flatness < 0.2 : false;
  // Full bbox >> core bbox ⇒ stray "tails" of gaussians outside the room core — typically
  // through-window outdoor content or pose drift.
  const tailRatio = s && maxCore > 0 ? maxDim / maxCore : 1;
  const tails = s ? tailRatio > 2 : false;
  const manyFloaters = s ? s.floaterFraction > 0.1 : false;
  const veryFaint = s ? s.faintFraction > 0.5 : false;

  return (
    <div className="space-y-4">
      <Breadcrumbs
        items={
          scanId
            ? [
                { label: "Projects", to: "/" },
                { label: room?.payload.name ?? "Room", to: room ? `/rooms/${room.id}` : undefined },
                { label: "Scan", to: `/scans/${scanId}` },
                { label: "Point cloud" },
              ]
            : [
                { label: "Models", to: "/models" },
                { label: "Point cloud" },
              ]
        }
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-ink">Photoreal model inspector</h1>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-full bg-cream p-1 text-sm">
            {((job?.meshUrl ? ["points", "splat", "mesh"] : ["points", "splat"]) as Array<"points" | "splat" | "mesh">).map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={`rounded-full px-4 py-1.5 font-medium capitalize transition ${
                  mode === m ? "bg-white text-accent shadow-card" : "text-muted"
                }`}
              >
                {m === "points" ? "Point cloud" : m === "splat" ? "Splat" : "Mesh"}
              </button>
            ))}
          </div>
          {job?.models && job.models.length > 1 && (
            <select
              className="rounded-full border border-black/10 bg-white px-3 py-1.5 text-sm"
              value={variant?.id ?? ""}
              onChange={(e) => setVariant(job.models!.find((m) => m.id === e.target.value) ?? variant)}
            >
              {job.models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                  {m.bytes ? ` · ${(m.bytes / 1e6).toFixed(0)} MB` : ""}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {jobErr && <div className="card p-4 text-sm text-red-500">Couldn't load the model: {jobErr}</div>}

      <div className="grid gap-4 lg:grid-cols-[1fr_300px]">
        <div className="card relative overflow-hidden" style={{ height: 620 }}>
          {mode === "mesh" && job?.meshUrl ? (
            <Canvas camera={{ position: [3, 2, 3], fov: 60, near: 0.01, far: 200 }} dpr={[1, 2]}>
              <color attach="background" args={["#0e0e12"]} />
              <OrbitControls makeDefault />
              <Suspense fallback={null}>
                <RoomMesh url={job.meshUrl} />
              </Suspense>
            </Canvas>
          ) : view ? (
            <Canvas
              key={`${variant?.id}-${flipUp}` /* refit camera on variant change / up flip */}
              camera={{ position: view.position.toArray(), fov: 55, near: 0.01, far: view.far, up: view.up.toArray() }}
              dpr={[1, 2]}
            >
              <color attach="background" args={["#0e0e12"]} />
              <ambientLight intensity={1} />
              <OrbitControls makeDefault target={view.center.toArray()} />
              {mode === "points" && data && (
                <PointCloud positions={data.positions} colors={data.colors} size={size} />
              )}
              {mode === "splat" && splatUrl && (
                <Suspense fallback={null}>
                  {/* drei renders the splat rotated 180° about X (it stores center as (x,−y,−z)).
                      Cancel it with the inverse 180°-about-X rotation so the splat shares the
                      point cloud's (correct) frame. Proper rotation → no mirror, no blank. */}
                  <group rotation={[Math.PI, 0, 0]}>
                    <Splat src={splatUrl} />
                  </group>
                </Suspense>
              )}
            </Canvas>
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-muted">
              {parseErr ? (
                <span className="text-red-500">Parse error: {parseErr}</span>
              ) : (
                <>
                  <Spinner />
                  <span className="text-sm">
                    {progress > 0 ? `Downloading model… ${Math.round(progress * 100)}%` : "Loading model…"}
                  </span>
                </>
              )}
            </div>
          )}
          {view && (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-between px-3 py-2 text-xs text-white/70">
              <span>drag to orbit · scroll to zoom</span>
              <span>{mode === "splat" ? "drei 3DGS renderer" : `${fmtN(data!.stats.count)} gaussians`}</span>
            </div>
          )}
        </div>

        <aside className="space-y-4">
          {mode === "points" && (
            <div className="card p-4">
              <div className="mb-2 text-sm font-semibold text-ink">Point size</div>
              <input type="range" min={0.003} max={0.05} step={0.001} value={size} onChange={(e) => setSize(+e.target.value)} className="w-full" />
              <label className="mt-3 flex items-center gap-2 text-sm text-ink">
                <input type="checkbox" checked={flipUp} onChange={(e) => setFlipUp(e.target.checked)} /> Flip up/down
              </label>
            </div>
          )}

          <div className="card p-4">
            <div className="mb-2 text-sm font-semibold text-ink">Model diagnostics</div>
            {s ? (
              <>
                <StatRow label="Gaussians" value={fmtN(s.count)} />
                <StatRow label="Proportions (norm.)" value={s.dims.map((d) => d.toFixed(2)).join(" × ")} />
                <StatRow label="Flatness (min/max)" value={flatness.toFixed(2)} warn={flat} />
                <StatRow label="Outlier spread (full/core)" value={`${tailRatio.toFixed(1)}×`} warn={tails} />
                <StatRow label="Floaters" value={`${fmtN(s.floaterCount)} (${fmtPct(s.floaterFraction)})`} warn={manyFloaters} />
                <StatRow label="Opacity (median)" value={s.opacityMedian.toFixed(2)} />
                <StatRow label="Nearly invisible" value={fmtPct(s.faintFraction)} warn={veryFaint} />
                <div className="mt-3 border-t border-cream pt-3 text-xs text-muted">
                  {flat && (
                    <p className="mb-1 text-red-500">
                      ⚠ Flat reconstruction (height ≪ footprint) — the walls/ceiling came out thin, so it
                      looks fuzzy from inside. Capture more up/ceiling views; reflective windows degrade the
                      upper surfaces.
                    </p>
                  )}
                  {tails && (
                    <p className="mb-1 text-red-500">
                      ⚠ Stray gaussians far outside the room core ({tailRatio.toFixed(1)}× the core size) —
                      usually through-window outdoor content or pose drift. Cropping the splat to the room
                      removes them.
                    </p>
                  )}
                  {manyFloaters && <p className="mb-1 text-red-500">⚠ &gt;10% floaters — stray gaussians hurt the view.</p>}
                  {veryFaint && <p className="mb-1 text-red-500">⚠ &gt;50% nearly-invisible gaussians.</p>}
                  {!flat && !tails && !manyFloaters && !veryFaint && (
                    <p>Clean + well-proportioned. If the point cloud reads as the room but the app looks fuzzy, it's the viewer — compare the Splat tab.</p>
                  )}
                  <p className="mt-2 text-[11px] text-muted/70">Sizes are normalized export units, not meters.</p>
                </div>
              </>
            ) : (
              <div className="text-sm text-muted">Loading stats…</div>
            )}
          </div>

          <div className="card p-4 text-xs text-muted">
            <p className="mb-1"><span className="font-semibold text-ink">Point cloud</span> = raw gaussian centers (our renderer). Tests the geometry.</p>
            <p><span className="font-semibold text-ink">Splat</span> = a faithful 3DGS render of the same file. Compare it with the iOS view to isolate viewer vs. model.</p>
          </div>
          <Link to={scanId ? `/scans/${scanId}` : "/models"} className="block text-center text-sm text-accent hover:underline">{scanId ? "← Back to scan" : "← Back to models"}</Link>
        </aside>
      </div>
    </div>
  );
}
